import { AnimateOnScroll } from "../../../components/animate";
import { AnimateWordsOnScroll } from "../../../components/animate/animateWords";
import { FloatY } from "../../../components/animate/floatY";
import { ScribbleReveal } from "../../../components/animate/scribbleReveal";
import { Icon } from "../../../components/icon/icon";
import { scribblePaths } from "../data/landingContent";

// Positions/sizes below are percentages of the reference 1440x814 Figma frame
// (left, width relative to 1440; top relative to 814) so they stay
// proportionally correct at any width once the section keeps that aspect ratio.
// floatDirection/floatDuration/floatDelay are staggered per icon so the
// cluster never floats in unison.
const floatingIcons = [
  {
    src: "/assets/images/landing-hero-smartphone.png",
    leftPct: (421.25 / 1440) * 100,
    topPct: (581.16 / 814) * 100,
    widthPct: (377.394 / 1440) * 100,
    rotate: "-10.81deg",
    flip: false,
    floatDirection: "up" as const,
    floatDuration: 12,
    floatDelay: 0,
  },
  {
    src: "/assets/images/landing-hero-paperplane-coin.png",
    leftPct: (280.32 / 1440) * 100,
    topPct: (643.21 / 814) * 100,
    widthPct: (282.22 / 1440) * 100,
    rotate: "180deg",
    flip: true,
    floatDirection: "down" as const,
    floatDuration: 13.5,
    floatDelay: 0.6,
  },
  {
    src: "/assets/images/landing-hero-three-coins.png",
    leftPct: (753.73 / 1440) * 100,
    topPct: (571.5 / 814) * 100,
    widthPct: (457.951 / 1440) * 100,
    rotate: "-171.58deg",
    flip: true,
    floatDirection: "up" as const,
    floatDuration: 14.5,
    floatDelay: 1.2,
  },
  {
    src: "/assets/images/landing-hero-hourglass.png",
    leftPct: (634.06 / 1440) * 100,
    topPct: (587.29 / 814) * 100,
    widthPct: (314.451 / 1440) * 100,
    rotate: "0deg",
    flip: false,
    floatDirection: "down" as const,
    floatDuration: 8,
    floatDelay: 0.3,
  },
];

export const Hero = ({
  onCreateEvent,
  navbarHeight,
}: {
  onCreateEvent: () => void;
  navbarHeight: number;
}) => {
  return (
    <section
      className="relative w-full overflow-hidden border-b border-[#d5dde8] bg-[linear-gradient(180deg,#d5e9fc_0%,#F4F7FA_81.731%)] lg:h-[100vh]"
      style={{ marginTop: -navbarHeight }}
    >
      <div className="relative z-[1] flex flex-col items-center gap-6 px-6 pb-10 pt-40 md:pt-16 lg:absolute lg:left-1/2 lg:top-1/3 lg:w-[700px] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:gap-[30px] lg:px-0 lg:pb-0">
        {/* <div className="rounded-[8px] bg-white px-3.5 py-1.5">
          <p className="font-onest text-sm text-ink lg:text-base">
            #1 ticketing app for event creators
          </p>
        </div> */}

        <div className="relative flex w-full flex-col items-center gap-4">
          <div className="flex flex-col items-center text-center font-dela not-italic leading-[0] text-[#060e35]">
            <AnimateWordsOnScroll
              as="p"
              startIndex={0}
              className="text-[32px] leading-[38px] tracking-[-0.64px] lg:text-[48px] lg:leading-[50px] lg:tracking-[-0.96px]"
            >
              Bring people
            </AnimateWordsOnScroll>
            <AnimateWordsOnScroll
              as="p"
              startIndex={2}
              className="text-[32px] leading-[38px] tracking-[-0.64px] lg:text-[48px] lg:leading-[50px] lg:tracking-[-0.96px]"
            >
              together. Get paid
            </AnimateWordsOnScroll>
            <AnimateWordsOnScroll
              as="p"
              startIndex={5}
              className="text-[32px] leading-[38px] tracking-[-0.64px] lg:text-[48px] lg:leading-[50px] lg:tracking-[-0.96px]"
            >
              the next day.
            </AnimateWordsOnScroll>
          </div>
          <AnimateOnScroll animation="blur-up" delay={0.6}>
            <p className="text-center font-onest text-[16px] leading-[22px] tracking-[-0.32px] text-ink lg:text-[20px] lg:leading-[28px] lg:tracking-[-0.4px]">
              <span className="block">
                From community hangouts to house parties and raves,
              </span>
              <span className="block">
                Tikket makes every event feel effortless.
              </span>
            </p>
          </AnimateOnScroll>

          {/* Positioned exactly like Figma's "yellow" scribble: absolute
              within the heading+subtext wrapper, left:39.65% top:50.9%
              width:34.78% of the 700x222 reference box, with height
              auto-derived from the scribble's own 243.45:41.29 aspect
              ratio so it can never stretch/distort. */}
          <ScribbleReveal
            {...scribblePaths.hero}
            delay={0.6}
            className="pointer-events-none absolute -z-10"
            style={{
              left: "39.65%",
              top: "50.9%",
              width: "34.78%",
              aspectRatio: "243.45 / 41.29",
            }}
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={onCreateEvent}
            className="rounded-full bg-gradient-to-r from-[#28315b] to-[#060e35] px-[18px] py-3 font-onest text-base font-medium text-white shadow-[inset_4px_4px_4px_0px_rgba(166,226,244,0.23)] transition-transform duration-300 hover:-translate-y-0.5"
          >
            Create your first event
          </button>

          <a
            href="/find-events"
            className="flex items-center gap-0.5 font-onest text-base font-medium text-ink underline"
          >
            Find events
            <Icon svg="arrow-right" width={16} height={16} />
          </a>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 hidden lg:block">
        {floatingIcons.map((icon) => (
          <div
            key={icon.src}
            className="absolute flex aspect-square items-center justify-center"
            style={{
              left: `${icon.leftPct}%`,
              top: `${icon.topPct}%`,
              width: `${icon.widthPct}%`,
            }}
          >
            <FloatY
              direction={icon.floatDirection}
              duration={icon.floatDuration}
              delay={icon.floatDelay}
              className="h-full w-full"
            >
              <img
                src={icon.src}
                alt=""
                aria-hidden
                className="h-full w-full object-contain"
                style={{
                  transform: `rotate(${icon.rotate}) ${icon.flip ? "scaleY(-1)" : ""}`,
                }}
              />
            </FloatY>
          </div>
        ))}
      </div>

      <div className="relative z-[1] mx-auto mt-10 aspect-[931/458] w-full max-w-[500px] pb-10 lg:hidden">
        {floatingIcons.map((icon) => {
          // Renormalize from the 1440x814 frame to the icon cluster's own
          // bounding box (280.32-1211.68 x, 571.5-1029.45 y) for the compact
          // mobile/tablet layout, matching the same relative arrangement.
          const clusterLeftPct =
            ((icon.leftPct / 100) * 1440 - 280.32) / 931.365;
          const clusterTopPct = ((icon.topPct / 100) * 814 - 571.5) / 457.951;
          const clusterWidthPct = ((icon.widthPct / 100) * 1440) / 931.365;

          return (
            <div
              key={icon.src}
              className="absolute flex aspect-square items-center justify-center"
              style={{
                left: `${clusterLeftPct * 100}%`,
                top: `${clusterTopPct * 100}%`,
                width: `${clusterWidthPct * 100}%`,
              }}
            >
              <FloatY
                direction={icon.floatDirection}
                duration={icon.floatDuration}
                delay={icon.floatDelay}
                className="h-full w-full"
              >
                <img
                  src={icon.src}
                  alt=""
                  aria-hidden
                  className="h-full w-full object-contain"
                  style={{
                    transform: `rotate(${icon.rotate}) ${icon.flip ? "scaleY(-1)" : ""}`,
                  }}
                />
              </FloatY>
            </div>
          );
        })}
      </div>
    </section>
  );
};
